import { ReactNode } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface OverlayContentProps {
  children: ReactNode;
  className?: string;
  isLoading?: boolean;
}

/**
 * OverlayContent - The ONLY scrollable zone in an overlay page.
 * 
 * CRITICAL RULE: Cards and components inside must NOT have their own scroll.
 * No nested ScrollAreas allowed inside children.
 * 
 * Design principles:
 * - Single scroll zone for entire content
 * - Skeleton loading state for instant open perception
 * - Adequate padding with room for sticky footer
 */
export function OverlayContent({ children, className, isLoading }: OverlayContentProps) {
  // Sheet layout contract: single-column only, no grids
  if (isLoading) {
    return (
      <ScrollArea className="flex-1 min-h-0">
        <div className="px-4 sm:px-6 py-4 space-y-6">
          {/* Section 1 skeleton - single column */}
          <div className="space-y-3">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
          </div>
          {/* Section 2 skeleton - single column */}
          <div className="space-y-3">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
            <Skeleton className="h-12" />
          </div>
          {/* Section 3 skeleton - single column */}
          <div className="space-y-3">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-32" />
          </div>
        </div>
      </ScrollArea>
    );
  }

  return (
    <ScrollArea className="flex-1 min-h-0">
      <div className={cn("px-4 sm:px-6 pt-4 pb-36 sm:pb-20", className)}>
        {children}
      </div>
    </ScrollArea>
  );
}
